import { getCarrierById } from '../../common/helpers/carriers';
import { modifyDate } from '../../common/helpers/modify-date';
import { formattedNumber } from '../../common/helpers/utils';
import { CapacityDataType, CapacityEntityEntry } from '../types';

export const weeklyDataMapperSOC = (template: any, selectedTerminal: string) => {
  const entry = template.entries.find((e) => e.capacityDataType === CapacityDataType.Total);
  return {
    id: template.id,
    date: `${template.date}`,
    moveCount: formattedNumber(entry.moves),
    moveCountStretchPercentage: entry.moveCountStretchPercentage,
    moveCountStretch: getAdjustedMoveCount(entry.moves, entry.moveCountStretchPercentage),
    moveCountCritical: getAdjustedMoveCount(entry.moves, entry.moveCountCriticalPercentage),
    moveCountCriticalPercentage: entry.moveCountCriticalPercentage,
    capacityDataType: entry.capacityDataType,
    overwrite: entry.overwrite,
    terminalCode: selectedTerminal,
    entries: template.entries,
    timestamp: template.timestamp,
  };
};

export const weeklyDataMapperYard = (template: any) => {
  const entry = template.entries.find((e) => e.capacityDataType === CapacityDataType.Total);
  return {
    id: template.id,
    date: `${template.date}`,
    yardCapacity: formattedNumber(entry.yardCapacity),
    reeferPlugs: formattedNumber(entry.reeferPlugs),
    imoUnits: formattedNumber(entry.imoUnits),
    imoUnitsDescription: entry.imoUnitsDescription,
    oogUnits: formattedNumber(entry.oogUnits),
    ladenCapacity: formattedNumber(entry.ladenCapacity),
    emptyCapacity: formattedNumber(entry.emptyCapacity),
    maxCapacity: formattedNumber(entry.maxCapacity),
    capacityDataType: entry.capacityDataType,
    overwrite: entry.overwrite,
    entries: template.entries,
  };
};

// @TODO: Replace types!
export const defaultDataMapperSOC =
  (capacityTemplateEntities: any, selectedTerminal: string) => (template: any, index: number) => {
    const entry = template.entries.find((e) => e.capacityDataType === CapacityDataType.Total);
    const previousStartDate = capacityTemplateEntities[index - 1]?.startDate?.split('T')[0];
    const startDate = template.startDate.split('T')[0];
    const nextStartDate = capacityTemplateEntities[index + 1]?.startDate?.split('T')[0];

    return {
      id: template.id,
      startDate: template.startDate,
      previousStartDate: previousStartDate,
      nextStartDate: nextStartDate,
      validityPeriod:
        startDate.replaceAll('-', '/') +
        ' - ' +
        (nextStartDate ? modifyDate(nextStartDate, 'remove').replaceAll('-', '/') : '2100/01/01'),
      moveCount: formattedNumber(entry.moves),
      moves: formattedNumber(entry.moves),
      moveCountStretchPercentage: entry.moveCountStretchPercentage,
      moveCountCriticalPercentage: entry.moveCountCriticalPercentage,
      moveCountStretch: getAdjustedMoveCount(entry.moves, entry.moveCountStretchPercentage),
      moveCountCritical: getAdjustedMoveCount(entry.moves, entry.moveCountCriticalPercentage),
      entries: [
        ...(fillCarriers(
          template.terminalCode,
          template.entries.filter((e) => e.capacityDataType !== CapacityDataType.Total),
          template.entries.filter((e) => e.capacityDataType === CapacityDataType.Total)?.moves,
        ) || []),
        ...template.entries.filter((e) => e.capacityDataType === CapacityDataType.Total),
      ],
      overwrite: entry.overwrite,
      status: entry.overwrite ? 'Adjusted' : 'Default',
      terminalCode: selectedTerminal,
    };
  };

export const defaultDataMapperYard = (capacityTemplateEntities: any) => (template: any, index: number) => {
  const entry = template.entries.find((e) => e.capacityDataType === CapacityDataType.Total);
  const previousStartDate = capacityTemplateEntities[index - 1]?.startDate?.split('T')[0];
  const startDate = template.startDate.split('T')[0];
  const nextStartDate = capacityTemplateEntities[index + 1]?.startDate?.split('T')[0];

  return {
    id: template.id,
    startDate: template.startDate,
    previousStartDate: previousStartDate,
    nextStartDate: nextStartDate,
    validityPeriod:
      startDate.replaceAll('-', '/') +
      ' - ' +
      (nextStartDate ? modifyDate(nextStartDate, 'remove').replaceAll('-', '/') : '2100/01/01'),
    yardCapacity: formattedNumber(entry.yardCapacity),
    reeferPlugs: formattedNumber(entry.reeferPlugs),
    imoUnits: formattedNumber(entry.imoUnits),
    imoUnitsDescription: entry.imoUnitsDescription,
    oogUnits: formattedNumber(entry.oogUnits),
    ladenCapacity: formattedNumber(entry.ladenCapacity),
    emptyCapacity: formattedNumber(entry.emptyCapacity),
    maxCapacity: formattedNumber(entry.maxCapacity),
    entries: [
      ...(fillCarriers(
        template.terminalCode,
        template.entries.filter((e) => e.capacityDataType !== CapacityDataType.Total),
        template.entries.filter((e) => e.capacityDataType === CapacityDataType.Total)?.moves,
      ) || []),
      ...template.entries.filter((e) => e.capacityDataType === CapacityDataType.Total),
    ],
    overwrite: entry.overwrite,
    status: entry.overwrite ? 'Adjusted' : 'Default',
  };
};

export const fillCarriers = (terminalCode, entries, totalMoves: number) => {
  return entries
    .filter((x) => x.capacityDataType !== CapacityDataType.Total)
    .sort((a, b) => getCarrierById(a.capacityDataType).order - getCarrierById(b.capacityDataType).order)
    .map((carrier) => ({ ...carrier, totalMoves, oogUnits: carrier.oogUnits || 0, imoUnits: carrier.imoUnits || 0 }));
};

const getAdjustedMoveCount = (moves: number, percentage: number): string => {
  return `${formattedNumber(moves + Math.ceil((moves * percentage) / 100))} (${percentage || 0}%)`;
};

export const getCarriersFieldSum = (entries: CapacityEntityEntry[], field: string) => {
  return entries.filter((e) => e.capacityDataType !== CapacityDataType.Total).reduce((acc, cur) => acc + cur[field], 0);
};

export const getCarriersMovesSum = (entries: CapacityEntityEntry[]) => {
  return getCarriersFieldSum(entries, 'moves');
};

export const editCarriersMapper = (type: 'soc' | 'yard', entries: CapacityEntityEntry[], data: any) => {
  const isSOC = type === 'soc';
  const res = entries
    .filter((e) => e.capacityDataType !== CapacityDataType.Total)
    .sort((a, b) => getCarrierById(a.capacityDataType).order - getCarrierById(b.capacityDataType).order)
    .map((e) => {
      return {
        id: e.capacityDataType,
        carrier: e.capacityDataType,
        moveCount: e.moves,
        yardCapacity: e.yardCapacity,
        reeferPlugs: e.reeferPlugs,
        imoUnits: e.imoUnits,
        oogUnits: e.oogUnits,
      };
    });

  const totalCarriersMoves = res.reduce((acc, cur) => acc + cur.moveCount, 0);
  const totalCarriersYardCapacity = res.reduce((acc, cur) => acc + cur.yardCapacity, 0);
  const totalCarriersReeferPlugs = res.reduce((acc, cur) => acc + cur.reeferPlugs, 0);
  const totalCarriersImoUnits = res.reduce((acc, cur) => acc + cur.imoUnits, 0);
  const totalCarriersOogUnits = res.reduce((acc, cur) => acc + cur.oogUnits, 0);

  if (isSOC) {
    res.unshift({
      // @ts-ignore
      id: 'total',
      // @ts-ignore
      moveCount: formattedNumber(data.totalMoveCount),
      moveCountStretchPercentage: data.stretch,
      moveCountCriticalPercentage: data.critical,
      moveCountStretch: getAdjustedMoveCount(data.totalMoveCount, data.stretch),
      moveCountCritical: getAdjustedMoveCount(data.totalMoveCount, data.critical),
    });
  } else {
    res.unshift({
      id: CapacityDataType.Total,
      // @ts-ignore
      yardCapacity: formattedNumber(data.yardCapacity),
      // @ts-ignore
      reeferPlugs: formattedNumber(data.reeferPlugs),
      // @ts-ignore
      imoUnits: formattedNumber(data.imoUnits),
      // @ts-ignore
      oogUnits: formattedNumber(data.oogUnits),
    });
  }
  res.push({
    // @ts-ignore
    id: 'residual',
    // @ts-ignore
    carrier: 'Residual',
    // @ts-ignore
    moveCount: formattedNumber(data.totalMoveCount - totalCarriersMoves),
    // @ts-ignore
    yardCapacity: formattedNumber(data.yardCapacity - totalCarriersYardCapacity),
    // @ts-ignore
    reeferPlugs: formattedNumber(data.reeferPlugs - totalCarriersReeferPlugs),
    // @ts-ignore
    imoUnits: formattedNumber(data.imoUnits - totalCarriersImoUnits),
    // @ts-ignore
    oogUnits: formattedNumber(data.oogUnits - totalCarriersOogUnits),
  });

  return res;
};

export const updateEntries = (
  entries: CapacityEntityEntry[],
  carrierId: string,
  field: string,
  value: string,
  totalMoves?: number,
) => {
  const index = entries.findIndex((e) => e.capacityDataType === carrierId);
  const entry = { ...entries[index] };
  let val = parseInt(value) || 0;
  if (field === 'percentage') {
    val = Math.round((parseInt(value) / 100) * (totalMoves || 0));
  }
  entry[field === 'percentage' ? 'moves' : field] = val;
  return [...entries.slice(0, index), entry, ...entries.slice(index + 1)];
};

export const addNewCarrier = (entries: CapacityEntityEntry[], carrierId: string) => {
  if (!carrierId) {
    return entries;
  }
  return [
    ...entries,
    {
      capacityDataType: carrierId,
      moves: 0,
      overwrite: false,
      moveCountStretchPercentage: 0,
      moveCountCriticalPercentage: 0,
      moveCountStretch: 0,
      moveCountCritical: 0,
      type: 'SOC',
    },
  ];
};

export const removeCarrier = (entries: CapacityEntityEntry[], carrierId: string) => {
  const index = entries.findIndex((e) => e.capacityDataType === carrierId);
  return [...entries.slice(0, index), ...entries.slice(index + 1)];
};

export const validateEntries = (entries: CapacityEntityEntry[], totalMoves: number) => {
  return totalMoves >= getCarriersMovesSum(entries);
};

export const validateEntriesForYard = (
  entries: CapacityEntityEntry[],
  data: { yardCapacity: number; reeferPlugs: number; imoUnits: number; oogUnits: number },
) => {
  const yardCapacityOk = data.yardCapacity >= getCarriersFieldSum(entries, 'yardCapacity');
  const reeferPlugsOk = data.reeferPlugs >= getCarriersFieldSum(entries, 'reeferPlugs');
  const imoOk = data.imoUnits >= getCarriersFieldSum(entries, 'imoUnits');
  const oogOk = data.oogUnits >= getCarriersFieldSum(entries, 'oogUnits');

  return yardCapacityOk && reeferPlugsOk && imoOk && oogOk;
};

export const recalculateEntries = (
  entries: CapacityEntityEntry[],
  defaultEntries: CapacityEntityEntry[],
  oldTotalMoves: number,
  newTotalMoves: number,
) => {
  const sum = getCarriersMovesSum(defaultEntries);
  const newResidual = newTotalMoves - sum;
  if (newResidual >= 0) {
    return defaultEntries;
  }
  const residualPercentageToShare = (oldTotalMoves - sum) / oldTotalMoves;
  const percentages = defaultEntries.map(
    (e) =>
      e.moves / oldTotalMoves +
      (e.capacityDataType === CapacityDataType.Total ? 0 : residualPercentageToShare * (e.moves / sum)),
  );
  let newEntries = entries.map((entry, index) => {
    return {
      ...entry,
      moves: Math.floor(newTotalMoves * (percentages[index] || 0)),
    };
  });

  const residualAfterDistribution = newTotalMoves - getCarriersMovesSum(newEntries);
  if (residualAfterDistribution > 0) {
    newEntries = newEntries.map((entry) => ({
      ...entry,
      moves: entry.capacityDataType === CapacityDataType.Maersk ? entry.moves + residualAfterDistribution : entry.moves,
    }));
  }

  return newEntries;
};

export const recalculateEntriesForYard = (
  entries: CapacityEntityEntry[],
  defaultEntries: CapacityEntityEntry[],
  field: string,
  oldValue: number,
  newValue: number,
) => {
  const sum = getCarriersFieldSum(defaultEntries, field);
  const oldResidual = oldValue - sum;
  const newResidual = newValue - sum;
  if (newResidual >= 0 && newResidual < oldResidual) {
    return defaultEntries;
  }

  const residualPercentageToShare = oldValue === 0 ? 0 : (oldValue - sum) / oldValue;
  const percentages = defaultEntries.map(
    (e) =>
      (oldValue === 0 ? 0 : e[field] / oldValue) +
      (e.capacityDataType === CapacityDataType.Total || sum === 0 ? 0 : residualPercentageToShare * (e[field] / sum)),
  );
  let newEntries = entries.map((entry, index) => {
    return {
      ...entry,
      [field]: Math.floor(newValue * (percentages[index] || 0)),
    };
  });
  const residualAfterDistribution = newValue - getCarriersFieldSum(newEntries, field);
  if (residualAfterDistribution > 0) {
    newEntries = newEntries.map((entry) => ({
      ...entry,
      [field]:
        entry.capacityDataType === CapacityDataType.Maersk ? entry[field] + residualAfterDistribution : entry[field],
    }));
  }

  return newEntries;
};

export const fillDefaultCarriers = (stretchPercentage: number, criticalPercentage: number) => {
  return [
    {
      capacityDataType: 'MAERSK',
      moveCountCriticalPercentage: criticalPercentage,
      moveCountStretchPercentage: stretchPercentage,
      moves: 0,
      overwrite: false,
      type: 'SOC',
    },
    {
      capacityDataType: 'GEMINI_MAERSK',
      moveCountCriticalPercentage: criticalPercentage,
      moveCountStretchPercentage: stretchPercentage,
      moves: 0,
      overwrite: false,
      type: 'SOC',
    },
    {
      capacityDataType: 'HAPAGLLOYD',
      moveCountCriticalPercentage: criticalPercentage,
      moveCountStretchPercentage: stretchPercentage,
      moves: 0,
      overwrite: false,
      type: 'SOC',
    },
    {
      capacityDataType: 'GEMINI_HAPAGLLOYD',
      moveCountCriticalPercentage: criticalPercentage,
      moveCountStretchPercentage: stretchPercentage,
      moves: 0,
      overwrite: false,
      type: 'SOC',
    },
  ];
};
